import * as React from "react";
import { cn } from "@/lib/utils";

export interface SearchableInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  endIcon?: React.ReactNode;
  name?: string;
  options: { value: string; label: string }[];
  onOptionSelect?: (selectedOption: { label: string; value: string }) => void;
  width?: string; // Added width prop
  dropdownPosition?: "left" | "right";
  displayDescription?: boolean // Added position prop
}

const SearchableInput = React.forwardRef<HTMLInputElement, SearchableInputProps>(
  ({ endIcon, type = "text", className, options, onOptionSelect, width = "100%", dropdownPosition = "left", ...props }, ref) => {
    const [query, setQuery] = React.useState(props.value || "");
    const [showDropdown, setShowDropdown] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      setQuery(props.value || "");
    }, [props.value]);

    // Close dropdown when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setShowDropdown(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      setShowDropdown(true);
      props.onChange?.(e);
    };

    const handleSelect = (selectedOption: { label: string; value: string }) => {
      setQuery(selectedOption.label);
      setShowDropdown(false);
      onOptionSelect?.(selectedOption);
    };

    return (
      <div className="relative" ref={containerRef}>
        {/* Input Field */}
        <input
          type={type}
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-2 text-sm ring-offset-background focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          ref={ref}
          value={query}
          onChange={handleSearch}
          onFocus={() => setShowDropdown(true)}
          {...props}
        />
        {endIcon && (
          <span className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
            {endIcon}
          </span>
        )}

        {/* Dropdown List */}
        {showDropdown && options.length > 0 && (
          <ul
            className={cn(
              "absolute bg-white border border-gray-300 rounded-md shadow-md mt-1 max-h-40 overflow-auto z-10 text-left",
              dropdownPosition === "right" ? "right-0" : "left-0"
            )}
            style={{width}}
          >
            {options.map((option) => (
              <li
                key={option.value}
                className="p-2 hover:bg-gray-200 cursor-pointer"
                onMouseDown={() => handleSelect(option)}
              >
                {option.label} {props.displayDescription && <span className="text-xs text-gray-500">({option.value})</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
);

SearchableInput.displayName = "SearchableInput";

export { SearchableInput };
